import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Badge } from '@components';
import { UserDetailsResponse } from '../types';

interface ViewUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string | null;
  onFetchUserDetails: (id: string) => Promise<UserDetailsResponse | null>;
}

export const ViewUserModal: React.FC<ViewUserModalProps> = ({
  isOpen,
  onClose,
  userId,
  onFetchUserDetails,
}) => {
  const { t } = useTranslation();
  const [userDetails, setUserDetails] = useState<UserDetailsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadUserDetails = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);
    try {
      const details = await onFetchUserDetails(userId);
      if (details) {
        setUserDetails(details);
      } else {
        setError('Failed to load user details');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  }, [userId, onFetchUserDetails]);

  useEffect(() => {
    if (isOpen && userId) {
      loadUserDetails();
    } else {
      setUserDetails(null);
      setError(null);
    }
  }, [isOpen, userId, loadUserDetails]);

  const getRoleBadge = (role: string) => {
    const roleLower = role?.toLowerCase();
    switch (roleLower) {
      case 'admin':
      case 'owner':
        return (
          <Badge variant="default" size="sm">
            {userDetails?.role_display || role}
          </Badge>
        );
      case 'manager':
        return (
          <Badge variant="info" size="sm">
            {userDetails?.role_display || role}
          </Badge>
        );
      case 'user':
        return (
          <Badge variant="secondary" size="sm">
            {userDetails?.role_display || role}
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" size="sm">
            {userDetails?.role_display || role}
          </Badge>
        );
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('users.userDetails') || 'User Details'}
      size="lg"
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">{t('common.loading')}</p>
          </div>
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <div className="text-red-600 dark:text-red-400 mb-4">
            <span className="material-icons text-4xl">error</span>
          </div>
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      ) : userDetails ? (
        <div className="space-y-6">
          {/* User Basic Information */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('users.basicInformation') || 'Basic Information'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('users.firstName')}
                </label>
                <p className="text-gray-900 dark:text-white">{userDetails.user.first_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('users.lastName')}
                </label>
                <p className="text-gray-900 dark:text-white">{userDetails.user.last_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('users.email')}
                </label>
                <p className="text-gray-900 dark:text-white">{userDetails.user.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('users.username') || 'Username'}
                </label>
                <p className="text-gray-900 dark:text-white">{userDetails.user.username}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('users.role')}
                </label>
                <div>{getRoleBadge(userDetails.role)}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('users.emailVerified') || 'Email Verified'}
                </label>
                <div>
                  <Badge
                    variant={userDetails.user.is_email_verified ? 'success' : 'warning'}
                    size="sm"
                  >
                    {userDetails.user.is_email_verified
                      ? t('common.yes') || 'Yes'
                      : t('common.no') || 'No'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Organization Information */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('users.organizationInformation') || 'Organization Information'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('users.role')}
                </label>
                <p className="text-gray-900 dark:text-white">{userDetails.role_display}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('users.joinedAt') || 'Joined At'}
                </label>
                <p className="text-gray-900 dark:text-white">{formatDate(userDetails.joined_at)}</p>
              </div>
              {userDetails.description && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {t('users.description') || 'Description'}
                  </label>
                  <p className="text-gray-900 dark:text-white">{userDetails.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Account Information */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('users.accountInformation') || 'Account Information'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('users.userId') || 'User ID'}
                </label>
                <p className="text-gray-900 dark:text-white">{userDetails.user.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('users.dateJoined') || 'Date Joined'}
                </label>
                <p className="text-gray-900 dark:text-white">
                  {formatDate(userDetails.user.date_joined)}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </Modal>
  );
};
